import { CommonModule, isPlatformBrowser } from "@angular/common";
import {
  Component,
  computed,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { AuthService } from "@services/auth.service";
import { EventsManagementService } from "@services/events-management.service";
import { GoogleMapsService } from "@services/google-maps.service";
import { SelectedCityService } from "@services/selected-city.service";
import { ButtonComponent } from "../../button/button.component";

@Component({
  selector: "app-city-dropdown",
  imports: [CommonModule, FormsModule, ButtonComponent],
  templateUrl: "./city-dropdown.component.html",
  styleUrl: "./city-dropdown.component.scss",
})
export class CityDropdownComponent implements OnInit {
  private readonly eventsService = inject(EventsManagementService);
  private readonly selectedCityService = inject(SelectedCityService);
  private readonly authService = inject(AuthService);
  private readonly googleMapsService = inject(GoogleMapsService);
  private readonly platformId = inject(PLATFORM_ID);

  readonly cities = signal<string[]>([]);
  readonly selectedCity = this.selectedCityService.selectedCity;
  readonly searchTerm = signal("");

  readonly filteredCities = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    if (!term) {
      return this.cities();
    }
    return this.cities().filter((city) => city.toLowerCase().includes(term));
  });

  ngOnInit(): void {
    this.eventsService.getActiveCities().subscribe((cities) => {
      this.cities.set(cities);
      this.applyDefaultCity();
    });
  }

  selectCity(city: string | null): void {
    this.selectedCityService.setCity(city);
    this.searchTerm.set("");
  }

  private applyDefaultCity(): void {
    if (this.selectedCity() !== null) {
      return;
    }

    const userCity = this.authService.getLocalStorageUserInfo()?.address
      ?.city_name;
    if (userCity) {
      this.selectCityFromName(userCity);
      return;
    }

    this.tryDetectCityFromGeolocation();
  }

  private selectCityFromName(name: string): void {
    const match = this.cities().find(
      (city) => city.toLowerCase() === name.toLowerCase(),
    );
    if (match) {
      this.selectedCityService.setCity(match);
    }
  }

  private tryDetectCityFromGeolocation(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        this.googleMapsService
          .reverseGeocode(latitude, longitude)
          .subscribe((cityName) => {
            if (cityName && this.selectedCity() === null) {
              this.selectCityFromName(cityName);
            }
          });
      },
      () => {
        /* permission denied or unavailable — silent */
      },
      { timeout: 5000, maximumAge: 60_000 },
    );
  }
}
